
import Vue from 'vue';
import '@/assets/css/helpdesk/helpdesk-theme.css';
import '@/assets/css/helpdesk/whatsapp.css';
import Component from 'vue-class-component';
import {namespace} from 'vuex-class';
import {IOnOffHandlers} from 'bonomo/dist';
import PaginationBar from '@/components/shared/PaginationBar.vue';
import HelpdeskAssignee, {colorForEmail} from '@/components/helpdesk/HelpdeskAssignee.vue';
import TicketCreateModal from '@/components/helpdesk/TicketCreateModal.vue';
import HelpdeskTicketModal from '@/components/helpdesk/HelpdeskTicketModal.vue';
import WhatsappChatThread from '@/components/helpdesk/WhatsappChatThread.vue';
import WhatsappComposer from '@/components/helpdesk/WhatsappComposer.vue';
import WhatsappConversationDetails from '@/components/helpdesk/WhatsappConversationDetails.vue';
import WhatsappTemplateDialog from '@/components/helpdesk/WhatsappTemplateDialog.vue';
import {
  EWhatsappConversationStatus, IWhatsappConversation, IWhatsappConversationFilters, IWhatsappMessage,
  IWhatsappStatus, IWhatsappTemplate, IWhatsappTemplateSend,
} from '@/model/Helpdesk/IWhatsapp';

const mod = namespace('helpdeskWhatsappModule');

// Polling: the open chat every tick, the list every LIST_EVERY ticks. Paused while the tab is hidden.
const POLL_MS = 5000;
const LIST_EVERY = 3;
// Inbound messages copied into the description of a ticket created from the chat.
const TICKET_MESSAGES = 10;

const escapeHtml = (text: string) => text
  .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

@Component({
  name: 'HelpdeskWhatsapp',
  components: {
    PaginationBar, HelpdeskAssignee, TicketCreateModal, HelpdeskTicketModal,
    WhatsappChatThread, WhatsappComposer, WhatsappConversationDetails, WhatsappTemplateDialog,
  },
})
export default class HelpdeskWhatsapp extends Vue {
  @mod.Getter('status')        private status!: IWhatsappStatus | null;
  @mod.Getter('conversations') private conversations!: IWhatsappConversation[];
  @mod.Getter('currentPage')   private currentPage!: number;
  @mod.Getter('lastPage')      private lastPage!: number;
  @mod.Getter('total')         private total!: number;
  @mod.Getter('perPage')       private perPage!: number;
  @mod.Getter('selected')      private selected!: IWhatsappConversation | null;
  @mod.Getter('messages')      private messages!: IWhatsappMessage[];
  @mod.Getter('hasOlder')      private hasOlder!: boolean;
  @mod.Getter('templates')     private templates!: IWhatsappTemplate[];

  @mod.Action('fetchStatus')         private fetchStatus!: () => void;
  @mod.Action('fetchConversations')  private fetchConversations!: (f: IWhatsappConversationFilters) => void;
  @mod.Action('pollConversations')   private pollConversations!: (f: IWhatsappConversationFilters) => Promise<void>;
  @mod.Action('selectConversation')  private selectConversation!: (c: IWhatsappConversation | null) => void;
  @mod.Action('fetchConversation')   private fetchConversation!: (id: number) => void;
  @mod.Action('pollConversation')    private pollConversation!: (id: number) => Promise<void>;
  @mod.Action('updateConversation')  private updateConversation!: (p: {id: number; data: Partial<IWhatsappConversation>}) => void;
  @mod.Action('markRead')            private markRead!: (id: number) => Promise<void>;
  @mod.Action('fetchMessages')       private fetchMessages!: (id: number) => void;
  @mod.Action('fetchOlderMessages')  private fetchOlderMessages!: (p: {id: number; beforeId: number}) => void;
  @mod.Action('pollMessages')        private pollMessages!: (id: number) => Promise<void>;
  @mod.Action('sendText')            private sendTextAction!: (p: {id: number; body: string}) => void;
  @mod.Action('sendMedia')           private sendMediaAction!: (p: {id: number; file: File; caption?: string}) => void;
  @mod.Action('sendTemplate')        private sendTemplateAction!: (p: {id: number; template: IWhatsappTemplateSend}) => void;
  @mod.Action('startConversation')   private startConversation!: (p: {phone: string; name?: string; template: IWhatsappTemplateSend}) => void;
  @mod.Action('fetchTemplates')      private fetchTemplates!: () => void;

  private statuses = Object.values(EWhatsappConversationStatus);
  private filterStatuses: EWhatsappConversationStatus[] = [EWhatsappConversationStatus.OPEN, EWhatsappConversationStatus.PENDING];
  // 'all' | 'me' | 'unassigned'
  private filterAssignee = 'all';
  private assigneeOptions = ['all', 'me', 'unassigned'];
  private assigneeLabelKeys: {[key: string]: string} = {all: 'everyone', me: 'mine', unassigned: 'unassigned'};
  private assigneeMenuOpen = false;
  private searchText = '';
  private page = 1;
  private searchTimer: any = null;

  private loading = false;
  private loadingMessages = false;
  private loadingOlder = false;
  private loadingTemplates = false;
  private sending = false;
  private saving = false;
  private sentCount = 0;
  private detailsOpen = false;

  private templateDialogOpen = false;
  private templateForNew = false;
  private ticketCreateOpen = false;
  private ticketModalOpen = false;
  private ticketModalId: number | null = null;

  private ons: IOnOffHandlers = {};
  private pollTimer: any = null;
  private pollTick = 0;

  get isMobile(): boolean {
    return this.$vuetify.breakpoint.width < 768;
  }

  // Room for the details as a third column.
  get isWide(): boolean {
    return this.$vuetify.breakpoint.width >= 1264;
  }

  get configured(): boolean {
    return !!this.status && this.status.enabled && this.status.configured;
  }

  get filters(): IWhatsappConversationFilters {
    return {
      page: this.page,
      per_page: this.perPage,
      search: this.searchText || undefined,
      statuses: this.filterStatuses,
      assignee: this.filterAssignee === 'all' ? undefined : this.filterAssignee,
    };
  }

  get ticketSubject(): string {
    return this.selected ? 'WhatsApp · ' + (this.selected.contact_name || this.selected.phone) : '';
  }

  // The customer's last messages, so the ticket can be understood without opening the chat.
  get ticketDescription(): string {
    if (!this.selected) return '';
    const lines = this.messages
      .filter((m) => m.direction === 'in' && m.body)
      .slice(-TICKET_MESSAGES)
      .map((m) => '<p>' + escapeHtml(m.body as string).replace(/\n/g, '<br>') + '</p>');
    const header = '<p><strong>WhatsApp</strong> ' + escapeHtml(this.selected.phone)
      + (this.selected.contact_name ? ' (' + escapeHtml(this.selected.contact_name) + ')' : '') + '</p>';
    return header + lines.join('');
  }

  created() {
    this.ons = this.$ons({
      'backend.call.OK.whatsappConversationsLoaded': () => { this.loading = false; },
      'backend.call.KO.whatsappConversationsLoaded': () => { this.loading = false; },
      'backend.call.OK.whatsappMessagesLoaded': () => { this.loadingMessages = false; },
      'backend.call.KO.whatsappMessagesLoaded': () => { this.loadingMessages = false; },
      'backend.call.OK.whatsappOlderMessagesLoaded': () => { this.loadingOlder = false; },
      'backend.call.KO.whatsappOlderMessagesLoaded': () => { this.loadingOlder = false; },
      'backend.call.OK.whatsappTemplatesLoaded': () => { this.loadingTemplates = false; },
      'backend.call.KO.whatsappTemplatesLoaded': () => { this.loadingTemplates = false; },
      'backend.call.OK.whatsappConversationLoaded': this.onDeepLinkLoaded,
      'backend.call.OK.whatsappMessageSent': this.onSent,
      'backend.call.KO.whatsappMessageSent': this.onSendFailed,
      'backend.call.OK.whatsappMediaSent': this.onSent,
      'backend.call.KO.whatsappMediaSent': this.onSendFailed,
      'backend.call.OK.whatsappTemplateSent': this.onTemplateSent,
      'backend.call.KO.whatsappTemplateSent': this.onSendFailed,
      'backend.call.OK.whatsappConversationStarted': this.onConversationStarted,
      'backend.call.KO.whatsappConversationStarted': this.onSendFailed,
      'backend.call.OK.whatsappConversationUpdated': () => { this.saving = false; },
      'backend.call.KO.whatsappConversationUpdated': () => { this.saving = false; },
    });
    this.selectConversation(null);
    this.fetchStatus();
    this.reload();
    this.openDeepLink();
    this.pollTimer = setInterval(this.poll, POLL_MS);
  }

  beforeDestroy() {
    clearInterval(this.pollTimer);
    clearTimeout(this.searchTimer);
    this.$offs(this.ons);
  }

  // ── List ───────────────────────────────────────────────
  reload() {
    this.page = 1;
    this.load();
  }

  load() {
    this.loading = true;
    this.fetchConversations(this.filters);
  }

  onPageChange(page: number) {
    this.page = page;
    this.load();
  }

  setStatuses(statuses: EWhatsappConversationStatus[]) {
    this.filterStatuses = statuses;
    this.reload();
  }

  setAssignee(assignee: string) {
    if (assignee === this.filterAssignee) return;
    this.filterAssignee = assignee;
    this.reload();
  }

  toggleStatus(status: EWhatsappConversationStatus) {
    const idx = this.filterStatuses.indexOf(status);
    this.setStatuses(idx === -1
      ? this.filterStatuses.concat(status)
      : this.filterStatuses.filter((s) => s !== status));
  }

  onSearchInput(value: string | null) {
    this.searchText = value || '';
    clearTimeout(this.searchTimer);
    this.searchTimer = setTimeout(this.reload, 400);
  }

  // ── Conversation ───────────────────────────────────────
  select(conversation: IWhatsappConversation | null) {
    this.selectConversation(conversation);
    this.detailsOpen = false;
    const query = conversation ? {c: String(conversation.id)} : {};
    if ((this.$route.query.c || '') !== (conversation ? String(conversation.id) : '')) {
      this.$router.replace({query}).catch(() => undefined);
    }
    if (!conversation) return;
    this.loadingMessages = true;
    this.fetchMessages(conversation.id);
    if (conversation.unread_count > 0) this.markRead(conversation.id);
  }

  // ?c=<id> opens that chat even if it isn't in the current page of the list.
  openDeepLink() {
    const id = Number(this.$route.query.c);
    if (id) this.fetchConversation(id);
  }

  onDeepLinkLoaded(conversation: IWhatsappConversation) {
    if (!this.selected && Number(this.$route.query.c) === conversation.id) this.select(conversation);
  }

  loadOlder() {
    if (!this.selected || !this.messages.length) return;
    this.loadingOlder = true;
    this.fetchOlderMessages({id: this.selected.id, beforeId: this.messages[0].id});
  }

  update(data: Partial<IWhatsappConversation>) {
    if (!this.selected) return;
    this.saving = true;
    this.updateConversation({id: this.selected.id, data});
  }

  poll() {
    if (document.hidden) return;
    this.pollTick++;
    const selected = this.selected;
    if (selected) this.pollMessages(selected.id);
    if (this.pollTick % LIST_EVERY === 0) this.refreshList();
  }

  // List + open chat. New messages that arrived in the chat being read are read already.
  private refreshList() {
    this.pollConversations(this.filters);
    const selected = this.selected;
    if (!selected) return;
    this.pollConversation(selected.id).then(() => {
      if (this.selected && this.selected.id === selected.id && this.selected.unread_count > 0) this.markRead(selected.id);
    });
  }

  // ── Sending ────────────────────────────────────────────
  sendText(body: string) {
    if (!this.selected) return;
    this.sending = true;
    this.sendTextAction({id: this.selected.id, body});
  }

  sendMedia(payload: {file: File; caption: string}) {
    if (!this.selected) return;
    this.sending = true;
    this.sendMediaAction({id: this.selected.id, file: payload.file, caption: payload.caption || undefined});
  }

  openNewConversation() {
    this.openTemplates(true);
  }

  openTemplates(forNew: boolean) {
    this.templateForNew = forNew;
    this.templateDialogOpen = true;
    if (!this.templates.length) {
      this.loadingTemplates = true;
      this.fetchTemplates();
    }
  }

  onTemplateSubmit(payload: {phone?: string; name?: string; template: IWhatsappTemplateSend}) {
    this.sending = true;
    if (this.templateForNew) {
      this.startConversation({phone: payload.phone as string, name: payload.name, template: payload.template});
    } else if (this.selected) {
      this.sendTemplateAction({id: this.selected.id, template: payload.template});
    }
  }

  onSent() {
    this.sending = false;
    this.sentCount++;
    this.refreshList();
  }

  onTemplateSent() {
    this.sending = false;
    this.templateDialogOpen = false;
    this.refreshList();
  }

  // Meta's rejections are stored as failed messages: show them (and why) right away.
  onSendFailed() {
    this.sending = false;
    if (this.selected) this.pollMessages(this.selected.id);
  }

  onConversationStarted(conversation: IWhatsappConversation) {
    this.sending = false;
    this.templateDialogOpen = false;
    this.$uiBus.emit('success', this.$t('helpdesk.whatsapp.conversationStarted'));
    this.select(conversation);
  }

  // ── Tickets ────────────────────────────────────────────
  onTicketCreated(ticket: {id: number} | undefined) {
    if (ticket && ticket.id) this.update({ticket_id: ticket.id});
  }

  openTicket(id: number) {
    this.ticketModalId = id;
    this.ticketModalOpen = true;
  }

  // ── Display helpers ────────────────────────────────────
  initials(c: IWhatsappConversation): string {
    const name = (c.contact_name || '').trim();
    if (!name) return '#';
    const words = name.split(/\s+/);
    return (words[0].charAt(0) + (words.length > 1 ? words[words.length - 1].charAt(0) : '')).toUpperCase();
  }

  avatarColor(c: IWhatsappConversation): string {
    return colorForEmail(c.wa_id);
  }

  preview(c: IWhatsappConversation): string {
    const m = c.last_message;
    if (!m) return '';
    if (m.body) return m.body;
    return this.$t('helpdesk.whatsapp.messageType.' + m.type) as string;
  }

  shortDate(date: string | null): string {
    if (!date) return '';
    const d = new Date(date);
    if (d.toDateString() === new Date().toDateString()) {
      return d.toLocaleTimeString('es-ES', {hour: '2-digit', minute: '2-digit'});
    }
    return d.toLocaleDateString('es-ES', {day: '2-digit', month: '2-digit', year: '2-digit'});
  }
}
